function getCartApiUrls() {
  const routesElement = document.querySelector('[data-kl-cart-routes]');
  const cartUrl = routesElement?.dataset.klCartUrl || '/cart';
  const changeUrl = routesElement?.dataset.klCartChangeUrl || '/cart/change';

  return { addUrl: `${cartUrl}/add.js`, changeUrl: `${changeUrl}.js` };
}

let isGiftRequestInFlight = false;

function getClampedPercent(rawValue) {
  const parsedValue = Number.parseFloat(rawValue);

  if (Number.isNaN(parsedValue)) return 0;
  if (parsedValue < 0) return 0;
  if (parsedValue > 100) return 100;

  return parsedValue;
}

function getCurrencyRate() {
  try {
    const rate = Number.parseFloat(Shopify.currency.rate);

    return Number.isNaN(rate) || rate <= 0 ? 1 : rate;
  } catch (error) {
    return 1;
  }
}

function getThresholdInCartCurrency(threshold, rate) {
  return threshold * 100 * rate;
}

const cartMoneyFormatters = new Map();

function formatCartMoney(amountInMinorUnits, currencyCode) {
  try {
    if (!cartMoneyFormatters.has(currencyCode)) {
      cartMoneyFormatters.set(
        currencyCode,
        new Intl.NumberFormat(undefined, {
          style: 'currency',
          currency: currencyCode,
          maximumFractionDigits: 0,
        }),
      );
    }

    return cartMoneyFormatters.get(currencyCode).format(Math.ceil(amountInMinorUnits / 100));
  } catch (error) {
    console.debug('Cart drawer could not format a reward amount', { currencyCode, error });
    return '';
  }
}

function getSteppedProgressPercent(thresholdsInCartCurrency, cartTotal) {
  if (!thresholdsInCartCurrency.length) return 0;

  const stepPercent = 100 / thresholdsInCartCurrency.length;
  let previousThreshold = 0;

  for (let index = 0; index < thresholdsInCartCurrency.length; index += 1) {
    const threshold = thresholdsInCartCurrency[index];

    if (cartTotal >= threshold) {
      previousThreshold = threshold;
      continue;
    }

    const stepSpan = threshold - previousThreshold;
    const stepRatio = stepSpan > 0 ? (cartTotal - previousThreshold) / stepSpan : 0;

    return getClampedPercent((index + stepRatio) * stepPercent);
  }

  return 100;
}

function getMixedReachedPercent(thresholdsInCartCurrency, cartTotal, eligibleTotal) {
  if (!thresholdsInCartCurrency.length) return 0;

  const stepPercent = 100 / thresholdsInCartCurrency.length;
  let lastReachedStep = 0;

  thresholdsInCartCurrency.forEach((threshold, index) => {
    const total = index === 0 ? eligibleTotal : cartTotal;

    if (threshold > 0 && total >= threshold) lastReachedStep = index + 1;
  });

  return getClampedPercent(lastReachedStep * stepPercent);
}

function applyRewardProgress(rewardsElement) {
  const fillElement = rewardsElement.querySelector('[data-kl-cart-rewards-fill]');
  const reachedElement = rewardsElement.querySelector('[data-kl-cart-rewards-reached]');
  const rewardElements = Array.from(rewardsElement.querySelectorAll('[data-kl-cart-reward]'));

  if (!fillElement) return;
  if (!rewardElements.length) return;

  const rate = getCurrencyRate();
  const currencyCode = rewardsElement.dataset.klCartRewardsCurrency;
  const cartTotal = Number.parseInt(rewardsElement.dataset.klCartRewardsTotal, 10) || 0;
  const eligibleTotal =
    Number.parseInt(rewardsElement.dataset.klCartRewardsEligibleTotal, 10) || 0;
  const awayTemplate = rewardsElement.dataset.klCartRewardsAwayTemplate || '';

  const thresholdsInCartCurrency = rewardElements.map((rewardElement) =>
    getThresholdInCartCurrency(Number.parseFloat(rewardElement.dataset.klCartRewardThreshold) || 0, rate),
  );

  const stepPercent = 100 / rewardElements.length;

  fillElement.style.setProperty(
    '--kl-cart-rewards-progress',
    `${getSteppedProgressPercent(thresholdsInCartCurrency, cartTotal)}%`,
  );

  if (reachedElement) {
    reachedElement.style.setProperty(
      '--kl-cart-rewards-reached',
      `${getMixedReachedPercent(thresholdsInCartCurrency, cartTotal, eligibleTotal)}%`,
    );
  }

  rewardElements.forEach((rewardElement, index) => {
    const positionPercent = getClampedPercent((index + 1) * stepPercent);

    rewardElement.style.setProperty('--kl-cart-reward-position', `${positionPercent}%`);

    const thresholdInCartCurrency = thresholdsInCartCurrency[index];
    const rewardTotal = index === 0 ? eligibleTotal : cartTotal;
    const isReached = thresholdInCartCurrency > 0 && rewardTotal >= thresholdInCartCurrency;
    const statusElement = rewardElement.querySelector('[data-kl-cart-reward-status]');

    rewardElement.classList.toggle('is-reached', isReached);

    if (!statusElement) return;

    const nextStatus = isReached
      ? ''
      : awayTemplate.replace(
          '[amount]',
          formatCartMoney(thresholdInCartCurrency - rewardTotal, currencyCode),
        );

    if (statusElement.textContent !== nextStatus) statusElement.textContent = nextStatus;
  });
}

async function postCartChange(url, requestBody) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(requestBody),
  });

  if (response.ok) return;
  let cartMessage = '';

  try {
    const errorBody = await response.json();

    cartMessage = errorBody.description || errorBody.message || '';
  } catch (error) {
    cartMessage = '(no JSON body)';
  }

  throw new Error(`Cart responded ${response.status}: ${cartMessage}`);
}

async function renderCartDrawer() {
  if (!document.querySelector('#CartDrawer')) {
    const renderDetail = { renderPromises: [] };

    document.dispatchEvent(new CustomEvent('kl:cart:render', { detail: renderDetail }));
    await Promise.all(renderDetail.renderPromises);
    return;
  }

  const sectionResponse = await fetch(`${window.location.pathname}?section_id=cart-drawer`);

  if (!sectionResponse.ok) throw new Error(`Section responded ${sectionResponse.status}`);

  const sectionHtml = await sectionResponse.text();
  const sectionDocument = new DOMParser().parseFromString(sectionHtml, 'text/html');
  const freshDrawer = sectionDocument.querySelector('#CartDrawer');
  const liveDrawer = document.querySelector('#CartDrawer');

  if (!freshDrawer || !liveDrawer) return;

  liveDrawer.innerHTML = freshDrawer.innerHTML;
}

async function setGiftInCart(giftElement, isWanted) {
  const variantId = giftElement.dataset.klCartGiftVariant;

  if (!variantId) return;

  if (isGiftRequestInFlight) return;

  isGiftRequestInFlight = true;

  const { addUrl, changeUrl } = getCartApiUrls();

  try {
    await postCartChange(
      isWanted ? addUrl : changeUrl,
      isWanted ? { items: [{ id: variantId, quantity: 1 }] } : { id: variantId, quantity: 0 },
    );

    await renderCartDrawer();
  } catch (error) {
    const toggleElement = giftElement.querySelector('[data-kl-cart-gift-toggle]');

    if (toggleElement) toggleElement.checked = !isWanted;

    console.debug('Cart drawer gift toggle failed', { variantId, isWanted, error });
  } finally {
    isGiftRequestInFlight = false;
  }
}

document.addEventListener(
  'change',
  (event) => {
    const toggleElement = event.target.closest('[data-kl-cart-gift-toggle]');

    if (!toggleElement) return;

    event.stopPropagation();

    const giftElement = toggleElement.closest('[data-kl-cart-gift]');

    if (!giftElement) return;

    setGiftInCart(giftElement, toggleElement.checked);
  },
  { capture: true },
);

document.addEventListener('click', (event) => {
  const closeElement = event.target.closest('[data-kl-cart-gift-note-close]');

  if (!closeElement) return;

  const noteElement = closeElement.closest('[data-kl-cart-gift-note]');

  if (noteElement) noteElement.open = false;
});

function getOpenGiftNote() {
  return document.querySelector('[data-kl-cart-gift-note][open]');
}

const FREE_GIFT_DECLINED_KEY_PREFIX = 'kl-cart-free-gift-declined-';

let isFreeGiftRequestInFlight = false;

function getFreeGiftDeclinedKey(variantId) {
  return `${FREE_GIFT_DECLINED_KEY_PREFIX}${variantId}`;
}

function getIsFreeGiftDeclined(variantId) {
  try {
    return sessionStorage.getItem(getFreeGiftDeclinedKey(variantId)) === 'true';
  } catch (error) {
    console.debug('Cart drawer could not read the free gift decline', { variantId, error });
    return false;
  }
}

function setIsFreeGiftDeclined(variantId, isDeclined) {
  try {
    if (isDeclined) {
      sessionStorage.setItem(getFreeGiftDeclinedKey(variantId), 'true');
    } else {
      sessionStorage.removeItem(getFreeGiftDeclinedKey(variantId));
    }
  } catch (error) {
    console.debug('Cart drawer could not record the free gift decline', { variantId, isDeclined, error });
  }
}

function getIsFreeGiftEarned(threshold, cartTotal, rate) {
  if (!threshold) return false;
  if (Number.isNaN(cartTotal)) return false;

  return cartTotal >= getThresholdInCartCurrency(threshold, rate);
}

function getFreeGiftAction({ isEarned, isInCart, isDeclined }) {
  if (isEarned && !isInCart && !isDeclined) return 'add';
  if (!isEarned && isInCart) return 'remove';

  return null;
}

function getFreeGiftPlan(stateElements, rate) {
  const variantIdsToAdd = [];
  const variantIdsToRemove = [];

  stateElements.forEach((stateElement) => {
    const variantId = stateElement.dataset.klCartFreeGiftVariant;

    if (!variantId) {
      console.debug(
        'Cart drawer free gift: the product resolved but has no purchasable variant. Check it is in stock, or has inventory tracking off.',
        { productId: stateElement.dataset.klCartFreeGiftProduct },
      );
      return;
    }

    const isEarned = getIsFreeGiftEarned(
      Number.parseInt(stateElement.dataset.klCartFreeGiftThreshold, 10),
      Number.parseInt(stateElement.dataset.klCartTotal, 10),
      rate,
    );

    if (!isEarned) setIsFreeGiftDeclined(variantId, false);

    const action = getFreeGiftAction({
      isEarned,
      isInCart: stateElement.dataset.klCartFreeGiftInCart === 'true',
      isDeclined: getIsFreeGiftDeclined(variantId),
    });

    if (action === 'add' && !variantIdsToAdd.includes(variantId)) variantIdsToAdd.push(variantId);
    if (action === 'remove' && !variantIdsToRemove.includes(variantId)) variantIdsToRemove.push(variantId);
  });

  return { variantIdsToAdd, variantIdsToRemove };
}

async function syncFreeGifts() {
  if (isFreeGiftRequestInFlight) return;

  const stateElements = Array.from(document.querySelectorAll('[data-kl-cart-free-gift]'));

  if (!stateElements.length) return;

  const { variantIdsToAdd, variantIdsToRemove } = getFreeGiftPlan(stateElements, getCurrencyRate());

  if (!variantIdsToAdd.length && !variantIdsToRemove.length) return;

  isFreeGiftRequestInFlight = true;

  const { addUrl, changeUrl } = getCartApiUrls();

  try {
    if (variantIdsToAdd.length) {
      await postCartChange(addUrl, {
        items: variantIdsToAdd.map((variantId) => ({ id: variantId, quantity: 1 })),
      });
    }

    for (const variantId of variantIdsToRemove) {
      await postCartChange(changeUrl, { id: variantId, quantity: 0 });
    }

    await renderCartDrawer();
  } catch (error) {
    console.debug('Cart drawer free gift sync failed', { variantIdsToAdd, variantIdsToRemove, error });
  } finally {
    isFreeGiftRequestInFlight = false;
  }
}

function applyShippingState(rowElement) {
  const threshold = Number.parseFloat(rowElement.dataset.klCartShippingThreshold) || 0;
  const eligibleTotal = Number.parseInt(rowElement.dataset.klCartShippingTotal, 10) || 0;

  const isFree =
    threshold > 0 && eligibleTotal >= getThresholdInCartCurrency(threshold, getCurrencyRate());

  rowElement.classList.toggle('is-free', isFree);
}

function initCartDrawer() {
  document.querySelectorAll('[data-kl-cart-rewards]').forEach(applyRewardProgress);
  document.querySelectorAll('[data-kl-cart-shipping]').forEach(applyShippingState);
  syncFreeGifts();
}

document.addEventListener('click', (event) => {
  const declineElement = event.target.closest('[data-kl-cart-free-gift-decline]');

  if (!declineElement) return;

  const variantId = declineElement.dataset.klCartFreeGiftDecline;

  if (!variantId) return;

  setIsFreeGiftDeclined(variantId, true);
});

initCartDrawer();

document.querySelectorAll('[data-kl-cart-drawer], [data-kl-cart-page]').forEach((cartRootElement) => {
  new MutationObserver(() => initCartDrawer()).observe(cartRootElement, {
    childList: true,
    subtree: true,
  });
});

document.addEventListener(
  'keyup',
  (event) => {
    if (event.code !== 'Escape') return;

    const openNoteElement = getOpenGiftNote();

    if (!openNoteElement) return;

    event.stopPropagation();
    openNoteElement.open = false;
  },
  { capture: true },
);

document.addEventListener('click', (event) => {
  const openNoteElement = getOpenGiftNote();

  if (!openNoteElement) return;
  if (openNoteElement.contains(event.target)) return;

  openNoteElement.open = false;
});

document.addEventListener('shopify:section:load', () => initCartDrawer());
